import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Typography from 'components/global/Typography';
import { ModalContent } from 'components/global/styled/Modal.styled';

import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';
import { formatPrice } from 'utils/utils';

import * as ModalStyled from '../ChargeAccountModal.styled';
import { CODWalletInput, PromoCodeInput } from './OrderSummary';
import * as S from './Tabs.styled';

const SelectAmount = ({ goToSuccessTab, setIsSectionCompleted }) => {
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const { Currency, LangCountryCode } = useLocale();
	const amountInputRef = useRef(null);
	const isTablet = useMediaQuery(Breakpoints.tablet);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const {
		remainingAmount,
		localization: { vatPercentage, chargeCreditOptions, minimumChargeAmount }
	} = useSelector((state) => state.user);
	const valueOptions = { shouldValidate: true, shouldDirty: true };

	const { setValue, watch } = formMethods;

	const subTotal = watch('subTotal');
	const selectedOption = watch('selectedOption');

	const handleValueChange = (value) => {
		const val = +value.toString?.().replace?.(/\D/g, '') || 0;
		const vat = +((val * vatPercentage) / 100).toFixed(2);
		setValue('subTotal', val, valueOptions);
		setValue('vatAmount', vat, valueOptions);
		setValue('total', +(val + vat).toFixed(2), valueOptions);
		if (watch('isPromoCodeApplied')) {
			setValue('isPromoCodeApplied', false, valueOptions);
			setValue('freeCredit', null, valueOptions);
			setValue('promoCode', null, valueOptions);
		}
	};

	const handleAmountChange = (e) => {
		if (e.target.value === '') {
			handleValueChange(0);
			return;
		}
		let value = e.target.value;
		value = +value.replace(/\D/g, '');
		handleValueChange(value);
	};

	const handleSelectOption = (value) => {
		if (value === selectedOption) {
			return;
		}
		setValue('selectedOption', value, valueOptions);
		handleValueChange(value === 'custom' ? 0 : value);
	};

	useEffect(() => {
		if (selectedOption === 'custom') {
			amountInputRef.current.focus();
		}
	}, [selectedOption]);

	useEffect(() => {
		setIsSectionCompleted(
			subTotal > 0 && ((minimumChargeAmount || 0) <= subTotal || watch('codWallet') === watch('total'))
		);
	}, [subTotal, watch('codWallet'), watch('total')]);

	const hasAmount = useMemo(() => subTotal > 0, [subTotal]);

	return (
		<ModalContent>
			{!isTablet && (
				<ModalStyled.TitleContainer>
					<Typography fontWeight="700" fontSize="var(--paragraph1-font-size)">
						chargeAccount.selectAmount
					</Typography>
					<Typography variant="caption" color="var(--content-3)">
						chargeAccount.selectAmountDescription
					</Typography>
				</ModalStyled.TitleContainer>
			)}
			<S.AmountWrapper>
				<S.CustomAmountContainer>
					<S.InputWrapper data-value={subTotal}>
						<S.AmountInput
							ref={amountInputRef}
							placeholder={0}
							value={subTotal ? formatPrice(subTotal, LangCountryCode) : ''}
							onChange={handleAmountChange}
							disabled={selectedOption !== 'custom'}
							maxLength={10}
							inputMode="numeric"
						/>
					</S.InputWrapper>
					<Typography variant="h4" color={'var(--black)'} style={{ opacity: hasAmount ? 1 : '0.4' }}>
						{Currency}
					</Typography>
				</S.CustomAmountContainer>
				<S.CurrentAmount>
					<Typography variant="p3" color="var(--content-4)" fontWeight="500">
						<Trans
							i18nKey="chargeAccount.currentCreditWithValue"
							values={{
								value: formatPrice(remainingAmount, LangCountryCode),
								currency: t(Currency)
							}}
							components={{
								blackText: <span style={{ color: 'var(--content-1)' }} />
							}}
						/>
					</Typography>
				</S.CurrentAmount>

				<S.AmountBottomContainer>
					<S.Seperator>
						<Typography variant="p1" color="var(--content-4)">
							or
						</Typography>
					</S.Seperator>

					<S.AmountContainer>
						{chargeCreditOptions?.split(',').map((option, index) => (
							<S.AmountCard
								key={index}
								selected={Number(option) === selectedOption}
								onClick={() => handleSelectOption(Number(option))}>
								<S.PointContainer>
									<Typography variant="p1" fontWeight="500" format="number">
										{Number(option)}
									</Typography>
								</S.PointContainer>
							</S.AmountCard>
						))}
						<S.AmountCard
							key={'custom'}
							selected={selectedOption === 'custom'}
							onClick={() => handleSelectOption('custom')}>
							<S.PointContainer>
								<Typography variant="p1" fontWeight="500">
									custom
								</Typography>
							</S.PointContainer>
						</S.AmountCard>
					</S.AmountContainer>
				</S.AmountBottomContainer>
			</S.AmountWrapper>

			{isMobile && (
				<S.ChargeAccountFooter>
					<CODWalletInput />
					<S.SummaryRow>
						<PromoCodeInput goToSuccessTab={goToSuccessTab} />
					</S.SummaryRow>
				</S.ChargeAccountFooter>
			)}
		</ModalContent>
	);
};

export default SelectAmount;
